<template>
  <BasePanel
    ref="panel"
    title="我的待办"
    icon="md-checkbox-outline"
    :tabs="tabs"
    :default-tab="activeTab"
    @on-tab-change="onTabChange"
  >
    <div slot="extra" class="panel-tasks-extra" @click.stop="openFlowPicker">
      <Icon type="md-add" :size="18" title="发起流程" />
    </div>

    <BaseEmpty
      v-if="loading"
      loading
      msg="加载中"
      style="background:var(--bg2);color:var(--subtext2)"
    />
    <BaseEmpty
      v-else-if="!items.length"
      :msg="emptyMsg"
      type="empty-task"
      style="background:var(--bg2);color:var(--subtext2)"
    />
    <div v-else class="panel-tasks-list">
      <div
        v-for="item in items"
        :key="item.id"
        class="panel-tasks-item"
        @click="openItem(item)"
      >
        <div class="panel-tasks-item-main">
          <div class="panel-tasks-item-name" :title="item.name">{{ item.name || '未命名' }}</div>
          <div class="panel-tasks-item-meta">
            <template v-if="activeTab === 'draft'">
              {{ formatTime(item.updated_at || item.created_at) }}
            </template>
            <template v-else>
              <span class="panel-tasks-status">{{ item.status || '—' }}</span>
              <span v-if="item.current_node"> · {{ item.current_node }}</span>
              <span> · {{ formatTime(item.updated_at || item.created_at) }}</span>
            </template>
          </div>
        </div>
        <Icon type="ios-arrow-forward" class="panel-tasks-arrow" />
      </div>
    </div>

    <Modal
      v-model="pickerVisible"
      title="选择要发起的流程"
      footer-hide
      :width="520"
      class-name="panel-flow-picker-modal"
    >
      <div class="panel-flow-picker">
        <Input
          v-model="pickerKeyword"
          search
          clearable
          placeholder="搜索流程名称"
          @on-search="loadInitiateFlows"
          @on-clear="loadInitiateFlows"
        />
        <div class="panel-flow-picker-body">
          <BaseEmpty v-if="pickerLoading" loading msg="加载中" />
          <BaseEmpty v-else-if="!pickerFlows.length" msg="暂无可发起的流程" />
          <div v-else class="panel-flow-picker-list">
            <div
              v-for="flow in filteredPickerFlows"
              :key="flow.id"
              class="panel-flow-picker-item"
              @click="startFlow(flow)"
            >
              <div
                class="panel-flow-picker-icon"
                :style="{ background: flow.color || 'var(--primary)' }"
              >
                <BaseIcon :icon="flow.icon || 'lianjieliu'" :size="20" color="#fff" />
              </div>
              <div class="panel-flow-picker-text">
                <div class="panel-flow-picker-name">{{ flow.name }}</div>
                <div class="panel-flow-picker-desc">{{ flow.description || '点击发起' }}</div>
              </div>
              <Icon type="ios-arrow-forward" />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  </BasePanel>
</template>

<script>
export default {
  panelInfo: {
    name: '我的待办',
    minWidthSpan: 8,
    minHeightSpan: 4,
  },
  data() {
    return {
      loading: false,
      items: [],
      activeTab: 'todo',
      counts: {todo: 0, created: 0, draft: 0},
      countsReady: false,
      pickerVisible: false,
      pickerLoading: false,
      pickerFlows: [],
      pickerKeyword: '',
    };
  },
  computed: {
    tabs() {
      return [{
        key: 'todo',
        title: '待处理',
        count: this.counts.todo,
      }, {
        key: 'created',
        title: '我发起的',
        count: this.counts.created,
      }, {
        key: 'draft',
        title: '草稿',
        count: this.counts.draft,
      }];
    },
    emptyMsg() {
      if (this.activeTab === 'todo') return '暂无待处理流程';
      if (this.activeTab === 'created') return '暂无未闭合的发起流程';
      return '暂无草稿';
    },
    filteredPickerFlows() {
      const kw = String(this.pickerKeyword || '').trim();
      if (!kw) return this.pickerFlows;
      return this.pickerFlows.filter((f) =>
        String(f.name || '').includes(kw) || String(f.description || '').includes(kw),
      );
    },
  },
  mounted() {
    this.$api.afterLogin(() => {
      this.bootstrap();
    });
  },
  methods: {
    formatTime(t) {
      if (!t) return '';
      const d = new Date(t);
      if (Number.isNaN(d.getTime())) return String(t);
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    preferTabWithCount(counts) {
      const order = ['todo', 'created', 'draft'];
      const hit = order.find((k) => (counts[k] || 0) > 0);
      return hit || 'todo';
    },
    async bootstrap() {
      await this.refreshCounts();
      const next = this.preferTabWithCount(this.counts);
      this.activeTab = next;
      this.$nextTick(() => {
        if (this.$refs.panel && this.$refs.panel.setTab) {
          this.$refs.panel.setTab(next);
        }
      });
      this.loadList();
    },
    refreshCounts() {
      const reqs = [
        this.$api.get('oa/flow-instances?q=mine&scope=todo&page=1&pagesize=1'),
        this.$api.get('oa/flow-instances?q=mine&scope=created&page=1&pagesize=1'),
        this.$api.get('oa/flow-drafts'),
      ];
      return Promise.all(reqs.map((p) => p.catch(() => null))).then(([todoRes, createdRes, draftRes]) => {
        const todoData = (todoRes && todoRes.data && todoRes.data.data) || {};
        const createdData = (createdRes && createdRes.data && createdRes.data.data) || {};
        const draftRaw = (draftRes && draftRes.data && draftRes.data.data) || [];
        const drafts = Array.isArray(draftRaw) ? draftRaw : (draftRaw.list || []);
        this.counts = {
          todo: Number(todoData.count) || 0,
          created: Number(createdData.count) || 0,
          draft: drafts.length,
        };
        this.countsReady = true;
      });
    },
    onTabChange(key) {
      if (this.activeTab === key) {
        this.loadList();
        return;
      }
      this.activeTab = key;
      this.loadList();
    },
    loadList() {
      this.loading = true;
      if (this.activeTab === 'draft') {
        this.$api.get('oa/flow-drafts').then((res) => {
          const raw = (res.data && res.data.data) || [];
          this.items = Array.isArray(raw) ? raw : (raw.list || []);
          this.$set(this.counts, 'draft', this.items.length);
        }).catch((e) => {
          this.Error(e);
          this.items = [];
        }).finally(() => {
          this.loading = false;
        });
        return;
      }
      const scope = this.activeTab === 'created' ? 'created' : 'todo';
      this.$api.get(`oa/flow-instances?q=mine&scope=${scope}&page=1&pagesize=50`).then((res) => {
        const data = (res.data && res.data.data) || {};
        this.items = data.data || [];
        this.$set(this.counts, scope, data.count != null ? data.count : this.items.length);
      }).catch((e) => {
        this.Error(e);
        this.items = [];
      }).finally(() => {
        this.loading = false;
      });
    },
    openItem(item) {
      if (!item) return;
      if (this.activeTab === 'draft') {
        if (!item.flow_id) return;
        const url = `/core/flows/${item.flow_id}/create`;
        this.$store.commit('session/push_flow_create', {
          id: item.flow_id,
          name: '流程创建-' + (item.name || ''),
        });
        this.RouteTo(url);
        return;
      }
      if (!item.id) return;
      this.$store.commit('session/push_flow_instance', {
        id: item.id,
        name: item.name || '流程实例',
      });
      this.RouteTo(`/core/flows/${item.id}/instance`);
    },
    openFlowPicker() {
      this.pickerVisible = true;
      this.pickerKeyword = '';
      this.loadInitiateFlows();
    },
    loadInitiateFlows() {
      this.pickerLoading = true;
      const kw = String(this.pickerKeyword || '').trim();
      let url = 'oa/flows?q=initiate';
      if (kw) url += `&keyword=${encodeURIComponent(kw)}`;
      this.$api.get(url).then((res) => {
        const raw = (res.data && res.data.data) || [];
        this.pickerFlows = Array.isArray(raw) ? raw : [];
      }).catch((e) => {
        this.Error(e);
        this.pickerFlows = [];
      }).finally(() => {
        this.pickerLoading = false;
      });
    },
    startFlow(flow) {
      if (!flow || !flow.id) return;
      this.pickerVisible = false;
      const url = `/core/flows/${flow.id}/create`;
      this.$store.commit('session/push_flow_create', {
        id: flow.id,
        name: '流程创建-' + (flow.name || ''),
      });
      this.RouteTo(url);
    },
  },
};
</script>

<style lang="less">
.panel-tasks-extra{
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  color: var(--text2);
  &:hover{
    color: var(--primary);
    background: var(--border);
  }
}
.panel-tasks-list{
  height: 100%;
  overflow-y: auto;
  padding: 6px 8px;
  background: var(--bg2);
}
.panel-tasks-item{
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  margin-bottom: 6px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg3);
  color: var(--text2);
  cursor: pointer;
  &:hover{
    border-color: var(--primary);
    .panel-tasks-item-name{ color: var(--primary); }
  }
}
.panel-tasks-item-main{
  flex: 1;
  min-width: 0;
}
.panel-tasks-item-name{
  font-size: 13px;
  font-weight: 500;
  color: var(--text2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.panel-tasks-item-meta{
  margin-top: 3px;
  font-size: 12px;
  color: var(--subtext2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.panel-tasks-status{
  color: var(--primary);
}
.panel-tasks-arrow{
  color: var(--subtext2);
  flex-shrink: 0;
}
.panel-flow-picker{
  min-height: 280px;
}
.panel-flow-picker-body{
  margin-top: 12px;
  max-height: 420px;
  overflow-y: auto;
  position: relative;
  min-height: 200px;
}
.panel-flow-picker-list{
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.panel-flow-picker-item{
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  cursor: pointer;
  background: var(--bg3);
  color: var(--text2);
  &:hover{
    border-color: var(--primary);
    filter: brightness(0.98);
  }
}
.panel-flow-picker-icon{
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.panel-flow-picker-text{
  flex: 1;
  min-width: 0;
}
.panel-flow-picker-name{
  font-size: 13px;
  font-weight: 500;
  color: var(--text2);
}
.panel-flow-picker-desc{
  margin-top: 2px;
  font-size: 12px;
  color: var(--subtext2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
